import { formatFileSize, formatDuration } from '../utils/formatUtils';
import {
    UPLOAD_STATUS,
    DATASET_STATUS,
    getStatusBadgeVariant,
    getStatusDisplayName
} from './uploadStatus';

// Model for upload summaries — builds grid rows and column config for the DataGrid.
class UploadSummary {
    constructor(summary) {
        this.status = summary?.status || '—';
        this.datasetName = summary?.datasetName || '—';
        this.totalFiles = summary?.totalFiles ?? 0;
        this.successfulFiles = summary?.successfulFiles ?? 0;
        this.failedFiles = summary?.failedFiles ?? 0;
        this.skippedFiles = summary?.skippedFiles ?? 0;
        this.cancelledFiles = summary?.cancelledFiles ?? 0;
        this.totalSize = summary?.totalSize ?? 0;
        this.uploadedSize = summary?.uploadedSize ?? 0;
        this.durationSeconds = summary?.durationSeconds ?? 0;
        this.uploadId = summary?.uploadId || '';
        this.startedAt = summary?.startedAt || '';
        this.completedAt = summary?.completedAt || '';
    }

    /**
     * Returns the column configuration for the summary DataGrid.
     * Static because columns never change — same for every summary.
     * @returns {object} EDS DataGrid columnConfig
     */
    static getColumnConfig() {
        return {
            uid: { displayName: 'Id', isPrimary: true, isHidden: true },
            field: {
                displayName: ' ',
                renderCellValue: (val) => `<span style="font-weight: 600;">${val}</span>`,
            },
            value: {
                displayName: ' ',
                renderCellValue: (val, row) => {
                    // Status cell: use centralized helpers for label + variant so badge colors are correct
                    if (row.field === 'Status') {
                        const variant = getStatusBadgeVariant(val);
                        const display = getStatusDisplayName(val);
                        return `<eds-badge value="${display}" variant="${variant}" size="medium"></eds-badge>`;
                    }

                    if (row.field === 'Successful Uploads' && parseInt(val, 10) > 0) {
                        return `<span style="font-weight: 600; color: #2e7d32;">${val}</span>`;
                    }
                    if (row.field === 'Failed Uploads' && parseInt(val, 10) > 0) {
                        return `<span style="font-weight: 600; color: #d32f2f;">${val}</span>`;
                    }
                    if (row.field === 'Cancelled Files' && parseInt(val, 10) > 0) {
                        return `<span style="font-weight: 600; color: #e65100;">${val}</span>`;
                    }
                    return `<span>${val}</span>`;
                },
            },
        };
    }

    /**
     * Builds and returns the grid rows array for the summary DataGrid.
     * The rows are DYNAMIC — they change based on the data:
     *   - "Cancelled Files" row only appears for aborted uploads with cancelled files
     *   - Row count varies (8-9 rows depending on status)
     * @returns {Array<object>} Array of row objects for EDS DataGrid
     */
    toGridRows() {
        const rows = [
            { uid: '1', field: 'Status', value: this.status },
            { uid: '2', field: 'Dataset Name', value: this.datasetName },
            { uid: '3', field: 'Total Files Scanned', value: String(this.totalFiles) },
            { uid: '4', field: 'Successful Uploads', value: String(this.successfulFiles) },
            { uid: '5', field: 'Failed Uploads', value: String(this.failedFiles) },
            { uid: '6', field: 'Skipped Files', value: String(this.skippedFiles) },
        ];

        // Dynamic row: only for aborted uploads with cancelled files
         const abortedLike = [
            DATASET_STATUS.UPLOAD_ABORTED,
            UPLOAD_STATUS.CANCELLED
        ].includes(this.status);

        if (abortedLike && this.cancelledFiles > 0) {
            rows.push({ uid: '6b', field: 'Cancelled Files', value: String(this.cancelledFiles) });
        }

        rows.push(
            { uid: '7', field: 'Total Files Uploaded', value: String(this.successfulFiles) },
            { uid: '8', field: 'Total Size', value: this.totalSize ? formatFileSize(this.totalSize) : '—' },
            { uid: '9', field: 'Total Time Taken', value: formatDuration(this.durationSeconds) },
        );

        return rows;
    }

    /**
     * Returns whether this is an aborted upload.
     * Used by the component to show/hide the disclaimer text.
     * @returns {boolean}
     */
    isAborted() {
        return [DATASET_STATUS.UPLOAD_ABORTED, UPLOAD_STATUS.CANCELLED].includes(this.status);
    }
}

export default UploadSummary;